import { useState, useEffect, useCallback } from 'react';
import { useApiUrl, useCustom } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import { getIdToken } from '@/providers/auth-provider';
import { toast } from '@/hooks/useToast';
import { ChevronLeft, Save, Loader2, AlertCircle, Plus, Trash2, Archive } from 'lucide-react';
import { OPTION_MODES, type Section, type QuestionDetail, type QuestionOption } from './constants';
import { QuestionForm, emptyQuestionForm, validateQuestionForm, formToBody, type QuestionFormState } from './QuestionForm';

// Small helper: authenticated JSON fetch that surfaces the backend error message.
async function apiFetch(apiUrl: string, path: string, method: string, body?: unknown): Promise<unknown> {
  const idToken = await getIdToken();
  const r = await fetch(`${apiUrl}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error((err as { message?: string; error?: string }).message ?? (err as { error?: string }).error ?? `HTTP ${r.status}`);
  }
  return r.status === 204 ? null : r.json();
}

export function QuestionEditPage() {
  const apiUrl = useApiUrl();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [form, setForm] = useState<QuestionFormState>(emptyQuestionForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: sectionsData } = useCustom<{ sections: Section[] }>({ url: '/v1/admin/sections', method: 'get' });
  const sections = sectionsData?.data?.sections ?? [];

  const { data, isLoading, refetch } = useCustom<QuestionDetail>({ url: `/v1/admin/questions/${id}`, method: 'get' });
  const detail = data?.data;

  useEffect(() => {
    if (detail) {
      setForm({
        section_id: detail.section_id ?? '',
        round: detail.round ?? 1,
        dimension: detail.dimension ?? '',
        weight: detail.weight ?? 3,
        response_mode: detail.response_mode ?? 'single_select',
        legacy_q_number: detail.legacy_q_number ?? '',
        main_number: String(detail.main_number ?? ''),
        sub_number: detail.sub_number != null ? String(detail.sub_number) : '',
        question_text: detail.question_text ?? '',
        question_text_es: detail.question_text_es ?? '',
        status: detail.status ?? 'active',
        question_order: String(detail.question_order ?? ''),
        show_summary_after: !!detail.show_summary_after,
        is_suggested: !!detail.is_suggested,
      });
    }
  }, [detail]);

  const usesOptions = OPTION_MODES.includes(form.response_mode as never);
  const validationError = validateQuestionForm(form, 'edit');

  const handleSave = async () => {
    if (validationError) { setError(validationError); return; }
    setSubmitting(true); setError(null);
    try {
      await apiFetch(apiUrl, `/v1/admin/questions/${id}`, 'PUT', formToBody(form, 'edit'));
      toast({ title: 'Question saved' });
      navigate('/questions');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async () => {
    setSubmitting(true); setError(null);
    try {
      await apiFetch(apiUrl, `/v1/admin/questions/${id}`, 'DELETE');
      toast({ title: 'Question archived' });
      navigate('/questions');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading || !detail) return <div className="p-6">Loading…</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <button onClick={() => navigate('/questions')}
          className="text-sm text-muted-foreground hover:text-foreground mb-2 inline-flex items-center gap-1">
          <ChevronLeft className="w-3 h-3" /> Back to questions
        </button>
        <h1 className="text-2xl font-semibold">Edit question</h1>
        <p className="text-sm text-muted-foreground mt-1">{detail.legacy_q_number} · {detail.section_title}</p>
      </div>

      <QuestionForm value={form} onChange={setForm} sections={sections} mode="edit" />

      {usesOptions && (
        <OptionsManager
          apiUrl={apiUrl}
          questionId={id!}
          options={detail.options ?? []}
          onChanged={() => refetch()}
          onError={setError}
        />
      )}

      {error && (
        <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-sm mt-5">
          <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
          <div className="text-destructive">{error}</div>
        </div>
      )}

      <div className="flex items-center justify-between pt-5 mt-5 border-t">
        <button type="button" onClick={handleArchive} disabled={submitting || detail.status === 'archived'}
          className="inline-flex items-center gap-1 px-3 py-2 border border-destructive/40 text-destructive rounded-md text-sm hover:bg-destructive/10 disabled:opacity-50">
          <Archive className="w-4 h-4" /> {detail.status === 'archived' ? 'Archived' : 'Archive'}
        </button>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate('/questions')} disabled={submitting}
            className="px-4 py-2 border rounded-md text-sm hover:bg-muted/50 disabled:opacity-50">Cancel</button>
          <button type="button" disabled={!!validationError || submitting} onClick={handleSave}
            className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Live options CRUD (each row calls the per-option endpoints immediately) ───
function OptionsManager({
  apiUrl, questionId, options, onChanged, onError,
}: {
  apiUrl: string;
  questionId: string;
  options: QuestionOption[];
  onChanged: () => void;
  onError: (msg: string) => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const run = useCallback(async (key: string, fn: () => Promise<unknown>) => {
    setBusyId(key); onError('');
    try { await fn(); onChanged(); }
    catch (err: unknown) { onError(err instanceof Error ? err.message : String(err)); }
    finally { setBusyId(null); }
  }, [onChanged, onError]);

  return (
    <div className="mt-6 border-t pt-5">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-medium">Options ({options.length})</h2>
        <button
          disabled={adding}
          onClick={() => run('add', async () => {
            setAdding(true);
            try {
              await apiFetch(apiUrl, `/v1/admin/questions/${questionId}/options`, 'POST', { option_text: 'New option' });
            } finally { setAdding(false); }
          })}
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline disabled:opacity-50">
          <Plus className="w-3.5 h-3.5" /> Add option
        </button>
      </div>
      {options.length === 0 && <p className="text-xs text-muted-foreground">No options yet.</p>}
      <div className="space-y-2">
        {options.map((opt) => (
          <OptionRow key={opt.option_id} apiUrl={apiUrl} opt={opt}
            busy={busyId === opt.option_id} run={run} />
        ))}
      </div>
    </div>
  );
}

function OptionRow({
  apiUrl, opt, busy, run,
}: {
  apiUrl: string;
  opt: QuestionOption;
  busy: boolean;
  run: (key: string, fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [label, setLabel] = useState(opt.option_text ?? '');
  const [labelEs, setLabelEs] = useState(opt.option_text_es ?? '');
  const [synonyms, setSynonyms] = useState((opt.synonyms ?? []).join(', '));
  const dirty = label !== (opt.option_text ?? '') || labelEs !== (opt.option_text_es ?? '') || synonyms !== (opt.synonyms ?? []).join(', ');
  const id = opt.option_id!;

  return (
    <div className="flex gap-2 items-start">
      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
        <input type="text" value={label} placeholder="Label (EN)" onChange={(e) => setLabel(e.target.value)}
          className="px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
        <input type="text" value={labelEs} placeholder="Label (ES)" onChange={(e) => setLabelEs(e.target.value)}
          className="px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
        <input type="text" value={synonyms} placeholder="synonyms, comma-separated" onChange={(e) => setSynonyms(e.target.value)}
          className="px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
      </div>
      <button title="Save option" disabled={!dirty || busy}
        onClick={() => run(id, () => apiFetch(apiUrl, `/v1/admin/options/${id}`, 'PUT', {
          option_text: label.trim(),
          option_text_es: labelEs.trim() || null,
          synonyms: synonyms.split(',').map((s) => s.trim()).filter(Boolean),
        }))}
        className="p-2 text-muted-foreground hover:text-primary disabled:opacity-30">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
      </button>
      <button title="Delete option" disabled={busy}
        onClick={() => run(id, () => apiFetch(apiUrl, `/v1/admin/options/${id}`, 'DELETE'))}
        className="p-2 text-muted-foreground hover:text-destructive disabled:opacity-30">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}
